import { motion } from 'motion/react'
import { useState, useEffect, useCallback } from 'react'
import { FocusTile } from '@/components/FocusTile'
import { HistoryStackManager } from '@/lib/historyStack'
import { createEmptySession, createEmptyTile, createEmptyAnswer, generateTileId } from '@/lib/storage'
import { saveSession, getTilesBySessionId, saveTile, saveAnswer, setLastSessionId, getResumeSession, Session, Tile } from '@/lib/storage'
import { useHermesBridge, delegateAndWait } from '@/lib/hermesBridge'

const MOCK_QUESTIONS = [
  {
    question: 'What is the main goal for this focus session?',
    suggestions: ['Deep work on core feature', 'Bug fixing & debugging', 'Code review & refactoring', 'Learning & research'],
  },
  {
    question: 'Which approach will you take?',
    suggestions: ['Top-down: architecture first', 'Bottom-up: implement & refine', 'Spike: throwaway prototype', 'Pair/mob programming'],
  },
  {
    question: 'What defines "done" for this session?',
    suggestions: ['Tests pass & PR ready', 'Feature deployed to staging', 'Documentation updated', 'Benchmark improved'],
  },
  {
    question: 'How will you handle blockers?',
    suggestions: ['Time-box: 15 min then escalate', 'Rubber duck / explain aloud', 'Switch to parallel task', 'Research & spike'],
  },
  {
    question: 'Session retrospective: What went well?',
    suggestions: ['Flow state achieved', 'Clear decisions made', 'Good progress on core', 'Learned something new'],
  },
]

export function FocusFlowView() {
  const [session, setSession] = useState<Session | null>(null)
  const [tiles, setTiles] = useState<Tile[]>([])
  const [currentTileIndex, setCurrentTileIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [historyManager, setHistoryManager] = useState<HistoryStackManager | null>(null)
  
  // Hermes Bridge
  const { isConnected: hermesConnected, lastError: hermesError, checkConnection: checkHermes } = useHermesBridge()
  const [delegating, setDelegating] = useState<string | null>(null)
  const [delegationResult, setDelegationResult] = useState<{ tileId: string; result: string } | null>(null)

  // Initialize session and tiles
  useEffect(() => {
    const init = async () => {
      // Try to resume existing session
      const resumeSession = await getResumeSession()
      
      if (!resumeSession) {
        // Create new session with mock questions
        const newSession = createEmptySession(
          generateTileId(),
          'Focus Flow Session',
          MOCK_QUESTIONS.length
        )
        await saveSession(newSession)
        
        // Create tiles for each question
        const newTiles = MOCK_QUESTIONS.map((q, i) => 
          createEmptyTile(newSession.id, i, q.question, q.suggestions)
        )
        for (const tile of newTiles) {
          await saveTile(tile)
        }
        
        const sessionTiles = await getTilesBySessionId(newSession.id)
        sessionTiles.sort((a, b) => a.index - b.index)
        
        setSession(newSession)
        setTiles(sessionTiles)
        setCurrentTileIndex(newSession.currentStep - 1)
        
        // Initialize history manager for current tile
        if (sessionTiles.length > 0) {
          const manager = await HistoryStackManager.create(newSession.id, sessionTiles[newSession.currentStep - 1]?.id || '')
          setHistoryManager(manager)
        }
        
        await setLastSessionId(newSession.id)
        setIsLoading(false)
        return
      }
      
      const sessionTiles = await getTilesBySessionId(resumeSession.id)
      sessionTiles.sort((a, b) => a.index - b.index)
      
      setSession(resumeSession)
      setTiles(sessionTiles)
      setCurrentTileIndex(resumeSession.currentStep - 1)
      
      // Initialize history manager for current tile
      if (sessionTiles.length > 0) {
        const manager = await HistoryStackManager.create(resumeSession.id, sessionTiles[resumeSession.currentStep - 1]?.id || '')
        setHistoryManager(manager)
      }
      
      await setLastSessionId(resumeSession.id)
      setIsLoading(false)
    }
    init()
  }, [])

  const currentTile = tiles[currentTileIndex]
  const canGoBack = currentTileIndex > 0
  const canGoForward = currentTileIndex < tiles.length - 1

  const handleAnswer = useCallback(async (value: string, type: 'suggestion' | 'freetext') => {
    if (!currentTile || !session) return
    
    // Save answer to storage
    const answer = createEmptyAnswer(currentTile.id, session.id, value, type)
    await saveAnswer(answer)
    
    // Update tile
    const updatedTile = {
      ...currentTile,
      answer: value,
      answerType: type,
      status: 'answered' as const,
      answeredAt: Date.now(),
      updatedAt: Date.now(),
    }
    await saveTile(updatedTile)
    
    // Update history manager
    if (historyManager) {
      historyManager.push(answer)
    }
    
    // Update local state
    setTiles(prev => prev.map(t => t.id === currentTile.id ? updatedTile : t))
  }, [currentTile, session, historyManager])

  const handleBack = useCallback(async () => {
    if (!canGoBack || !currentTile || !session) return
    
    // Update current tile status
    await saveTile({ ...currentTile, status: 'pending' as const })
    
    setCurrentTileIndex(prev => prev - 1)
    
    // Update session
    const newIndex = currentTileIndex - 1
    const updatedSession = { ...session, currentStep: newIndex + 1, updatedAt: Date.now() }
    await saveSession(updatedSession)
    setSession(updatedSession)
    
    // Initialize history manager for new tile
    const newTile = tiles[newIndex]
    if (newTile) {
      const manager = await HistoryStackManager.create(session.id, newTile.id)
      setHistoryManager(manager)
    }
  }, [canGoBack, currentTile, currentTileIndex, session, tiles])

  const handleRecalculate = useCallback(async () => {
    if (!currentTile || !session) return
    
    // Mark tile as recalculating
    const recalculatingTile = { ...currentTile, isRecalculating: true, status: 'recalculating' as const }
    await saveTile(recalculatingTile)
    setTiles(prev => prev.map(t => t.id === currentTile.id ? recalculatingTile : t))
    
    // Simulate recalculation
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    // Mark as answered again
    const updatedTile = { 
      ...recalculatingTile, 
      isRecalculating: false, 
      status: 'answered' as const,
      updatedAt: Date.now(),
    }
    await saveTile(updatedTile)
    setTiles(prev => prev.map(t => t.id === currentTile.id ? updatedTile : t))
    
    // Mark dependent tiles as recalculating
    if (historyManager) {
      historyManager.markDependentsRecalculating(currentTileIndex, tiles)
    }
  }, [currentTile, session, currentTileIndex, tiles, historyManager])

  const handlePause = useCallback(async () => {
    if (!session) return
    const pausedSession = { ...session, status: 'paused' as const, updatedAt: Date.now() }
    await saveSession(pausedSession)
    setSession(pausedSession)
  }, [session])

  const handleDelegateToHermes = useCallback(async () => {
    if (!currentTile || !session || delegating) return
    
    setDelegating(currentTile.id)
    setDelegationResult(null)
    
    try {
      // Build context from current session state
      const answeredTiles = tiles.filter(t => t.status === 'answered' && t.answer)
      const context = `
Current Focus Session: ${session.title}
Current Step: ${currentTileIndex + 1} of ${tiles.length}
Current Question: ${currentTile.question}
Previous Answers:
${answeredTiles.map(t => `- ${t.question}: ${t.answer}`).join('\n') || 'None yet'}
Remaining Questions:
${tiles.slice(currentTileIndex + 1).map(t => `- ${t.question}`).join('\n') || 'None'}

Task: Help complete the current focus step. Provide a concrete answer, approach, or solution for: "${currentTile.question}"
      `.trim()

      const result = await delegateAndWait(
        `Complete focus step: ${currentTile.question}`,
        context,
        {
          timeoutMs: 180000,
          onProgress: (status) => {
            // Could show progress in UI
            console.log('Delegation progress:', status.status)
          }
        }
      )

      const answer = result.result || result.summary || 'Hermes completed the task but returned no content.'
      setDelegationResult({ tileId: currentTile.id, result: answer })
      
      // Auto-fill the answer
      await handleAnswer(answer, 'suggestion')
      
    } catch (error) {
      console.error('Delegation failed:', error)
      setDelegationResult({ 
        tileId: currentTile.id, 
        result: `Delegation failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
      })
    } finally {
      setDelegating(null)
    }
  }, [currentTile, session, currentTileIndex, tiles, delegating, handleAnswer])

  const handleUndo = useCallback(async () => {
    if (!historyManager || !currentTile || !session) return
    
    const undone = historyManager.undo()
    if (!undone) return
    
    // Remove the undone answer
    await saveTile({ ...currentTile, answer: undefined, answerType: undefined, status: 'active' as const, answeredAt: undefined })
    setTiles(prev => prev.map(t => t.id === currentTile.id ? { ...t, answer: undefined, answerType: undefined, status: 'active' as const, answeredAt: undefined } : t))
  }, [historyManager, currentTile, session])

  const handleRedo = useCallback(async () => {
    if (!historyManager || !currentTile || !session) return
    
    const redone = historyManager.redo()
    if (!redone) return
    
    // Restore the redone answer
    const updatedTile = { 
      ...currentTile, 
      answer: redone.value, 
      answerType: redone.type, 
      status: 'answered' as const,
      answeredAt: Date.now(),
    }
    await saveTile(updatedTile)
    setTiles(prev => prev.map(t => t.id === currentTile.id ? updatedTile : t))
  }, [historyManager, currentTile, session])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-text-secondary">Loading focus flow…</p>
        </div>
      </div>
    )
  }

  if (!currentTile) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-text-secondary">No questions available. Create a session first.</p>
        </div>
      </div>
    )
  }

  const progress = (currentTileIndex + 1) / tiles.length

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex h-16 items-center justify-between px-6 border-b border-border">
        <h1 className="text-xl font-semibold">Focus Flow</h1>
        <div className="flex items-center gap-4">
          {/* Hermes Connection Status */}
          <div className="flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium transition-colors"
               style={{ backgroundColor: hermesConnected ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: hermesConnected ? '#22c55e' : '#ef4444' }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: hermesConnected ? '#22c55e' : '#ef4444' }} />
            {hermesConnected ? 'Hermes Connected' : 'Hermes Disconnected'}
            {hermesError && <span className="ml-1">⚠</span>}
          </div>
          <button 
            onClick={checkHermes} 
            className="btn-secondary text-xs"
            title="Reconnect to Hermes"
          >
            ⟳
          </button>
          <span className="text-sm text-text-muted">
            Step {currentTileIndex + 1} of {tiles.length}
          </span>
          {session && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              session.status === 'active' ? 'bg-green-500/20 text-green-400' :
              session.status === 'paused' ? 'bg-amber-500/20 text-amber-400' :
              'bg-blue-500/20 text-blue-400'
            }`}>
              {session.status}
            </span>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-3xl">
          <FocusTile
            question={currentTile.question}
            suggestions={currentTile.suggestions}
            answer={currentTile.answer}
            progress={progress}
            currentStep={currentTileIndex + 1}
            totalSteps={tiles.length}
            onAnswer={handleAnswer}
            onBack={handleBack}
            onRecalculate={handleRecalculate}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            isRecalculating={currentTile.isRecalculating}
            onPause={handlePause}
          />
        </div>
      </main>

      {/* Footer - Undo/Redo, Hermes Delegation & Session actions */}
      <footer className="flex h-16 items-center justify-between px-6 border-t border-border">
        <div className="flex items-center gap-2">
          <button
            onClick={handleUndo}
            disabled={!historyManager?.canUndo()}
            className="btn-secondary opacity-50 hover:opacity-100 disabled:opacity-30"
            title="Undo (Ctrl+Z)"
          >
            <UndoIcon className="w-4 h-4 mr-1" /> Undo
          </button>
          <button
            onClick={handleRedo}
            disabled={!historyManager?.canRedo()}
            className="btn-secondary opacity-50 hover:opacity-100 disabled:opacity-30"
            title="Redo (Ctrl+Y)"
          >
            <RedoIcon className="w-4 h-4 mr-1" /> Redo
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* Delegate to Hermes Button */}
          {hermesConnected && currentTile && currentTile.status !== 'answered' && (
            <button
              onClick={handleDelegateToHermes}
              disabled={!!delegating}
              className="btn-secondary gap-2"
              style={{ backgroundColor: delegating ? 'rgba(59, 130, 246, 0.2)' : undefined }}
            >
              {delegating === currentTile.id ? (
                <>
                  <span className="w-4 h-4 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
                  Delegating…
                </>
              ) : (
                <>
                  <ZapIcon className="w-4 h-4" />
                  Delegate to Hermes
                </>
              )}
            </button>
          )}
          {/* Delegation Result Toast */}
          {delegationResult && delegationResult.tileId === currentTile?.id && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed bottom-20 right-6 z-50 max-w-md px-4 py-3 rounded-xl shadow-lg bg-surface border border-border text-sm"
            >
              <div className="font-medium text-text-primary mb-1">Hermes Result</div>
              <div className="text-text-secondary whitespace-pre-wrap max-h-40 overflow-auto">{delegationResult.result}</div>
            </motion.div>
          )}
          {session?.status === 'paused' && (
            <button className="btn-primary">Resume Session</button>
          )}
          {session?.status === 'active' && tiles.every(t => t.status === 'answered') && (
            <button className="btn-primary">
              Complete Session
            </button>
          )}
        </div>
      </footer>
    </div>
  )
}

function UndoIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
    </svg>
  )
}

function RedoIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
    </svg>
  )
}

function ZapIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  )
}